  'use strict';

import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import heic2any from 'heic2any';
import * as pdfjsLib from 'pdfjs-dist';

import { db, handleDexieError } from '../../_utils/db/db';
import { builderDispatch, WebUI } from '../../_utils/ui';
import { getViewport } from '../../_utils/hoc/with-viewport-size';
import { usePrismItemStore } from 'stores/usePrismItemStore';
import { compressImage } from '../../_utils/db/image-manip-helpers';
import { uploadFileToAPI } from '../../_utils/db/observables';
import { uploadUrl, fireAddPhotos } from '../desktop/lib/add-photos-interface';
import ImageUploadFileValidator from '../../_utils/image-upload-file-validator';
import readFileAsArrayBuffer from '../../_utils/readFileAsArrayBuffer';
import { sessionTracker } from '../utils/session-tracker';

// --------------------------------------------------------------------

const fileValidator = new ImageUploadFileValidator();

const ensurePdfWorker = () => {
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
  }
};

/**
 * Dispatches a failed upload event and updates the store with the failed file name.
 * @param {string} fileName - The name of the file that failed to upload.
 */
const dispatchAndDisplayFailedUpload = (fileName) => {
  builderDispatch.dispatch({
    type: WebUI.UPLOAD_PROGRESS,
  });

  usePrismItemStore.getState().trackProgress();

  usePrismItemStore.setState(({ failedImagesUploaded }) => ({
    failedImagesUploaded: [...failedImagesUploaded, fileName],
  }));
};

/**
 * Converts HEIC/HEIF files to JPEG format if needed.
 * @param {File} file - The file to potentially convert.
 * @returns {Promise<File>} The converted file or original file.
 */
const convertHEICIfNeeded = async (file) => {
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 1,
    });
    return new File([convertedBlob], `${file.name}.jpg`, {
      type: 'image/jpeg',
    });
  }
  return file;
};

/**
 * Processes a file and prepares it for upload to IndexedDB.
 * @param {File} originalFile - The original uncompressed file.
 * @param {File} processedFile - The processed file (converted if needed).
 * @param {number|string} prismItemID - The prism item ID.
 * @returns {Promise<Object>} The file data object.
 */
const processFileAndUpload = async (originalFile, processedFile, prismItemID) => {
  if (!prismItemID) {
    throw new Error('processFileAndUpload: prismItemID is required');
  }

  const fileToUpload = processedFile || originalFile;

  const [compressedImageForThumbnails, compressedFileForUpload] = await Promise.all([
    compressImage(fileToUpload, { maxWidthOrHeight: 512 }),
    compressImage(fileToUpload),
  ]);

  sessionTracker.trackUploadedImage({
    // Mirrors the upload, which falls back to the uncompressed file.
    sentBytes: (compressedFileForUpload || fileToUpload).size,
    // A PDF page arrives with the whole PDF as its original, which isn't a
    // per-image size, so only image files report what the user picked.
    pickedBytes:
      originalFile.type === 'application/pdf' ? undefined : originalFile.size,
  });

  const fileData = {
    file: fileToUpload,
    compressedImage: compressedImageForThumbnails,
    compressedFile: compressedFileForUpload,
    status: 'pending',
    uploadUrl: uploadUrl(),
    prismItemID,
    prismItemIDs: [],
    createdAt: new Date(),
  };

  try {
    fileData.id = await db.imageUpload.add(fileData);
  } catch (e) {
    handleDexieError(e, { from: 'processFileAndUpload' });
    console.warn(`[upload] ${fileToUpload.name} — IndexedDB full, uploading directly`);
    const compressedBlobAsFile = new File(
      [compressedFileForUpload],
      fileToUpload.name,
      { type: fileToUpload.type }
    );
    await uploadFileToAPI({ ...fileData, idbSkipped: true }, compressedBlobAsFile);
  }

  // Track progress so UPLOAD_DONE fires when all files are processed
  builderDispatch.dispatch({ type: WebUI.UPLOAD_PROGRESS });
  usePrismItemStore.getState().trackProgress();

  return fileData;
};

/**
 * Processes a PDF file by converting each page to an image.
 * @param {File} pdfFile - The PDF file to process.
 * @param {number|string} prismItemID - The prism item ID.
 */
const processPDF = async (pdfFile, prismItemID) => {
  const pdfData = await readFileAsArrayBuffer(pdfFile);

  try {
    ensurePdfWorker();
    // pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

    const pdfDoc = await pdfjsLib.getDocument({ data: pdfData }).promise;
    const numPages = pdfDoc.numPages;

    const canvasPDF = document.createElement('canvas');
    canvasPDF.style.display = 'none';
    document.body.appendChild(canvasPDF);

    const ctx = canvasPDF.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D canvas context');
    }

    try {
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2 });

        canvasPDF.width = viewport.width;
        canvasPDF.height = viewport.height;

        await page.render({
          canvasContext: ctx,
          viewport,
          background: 'rgba(0,0,0,0)',
        }).promise;

        const blob = await new Promise((resolve, reject) =>
          canvasPDF.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('canvas.toBlob() returned null'));
          }, 'image/png')
        );

        const fileName = `${pdfFile.name}-${pageNum}.png`;

        const newFile = new File([blob], fileName, { type: 'image/png' });
        await processFileAndUpload(pdfFile, newFile, prismItemID);
      }
    } finally {
      // 💥 always runs, even if rendering page 7 fails
      canvasPDF.remove();
    }
  } catch (error) {
    dispatchAndDisplayFailedUpload(pdfFile.name);
    console.error('Error processing PDF:', error);
  }
};


/**
 * Processes a regular image file.
 * @param {File} imageFile - The image file to process.
 * @param {number|string} prismItemID - The prism item ID.
 */
const processImage = async (imageFile, prismItemID) => {
  const convertedFile = await convertHEICIfNeeded(imageFile);
  await processFileAndUpload(imageFile, convertedFile, prismItemID);
};

/**
 * Adds files to the upload queue, processing them as needed.
 * @param {File[]} files - Array of files to process and upload.
 */
const addFiles = async (files) => {
  const filteredFiles = files.filter(fileValidator.isAcceptedFileType);
  const { prismItem } = usePrismItemStore.getState();
  const prismItemID = prismItem?.num;

  if (!prismItemID) {
    throw new Error('addFiles: prismItemID is missing');
  }

  const totalUploads = await getTotalUploadCount(filteredFiles);

  if (filteredFiles.length > 0) {
    const batchId = `upl-${Date.now()}-${Math.round(Math.random() * 1e9)}`;

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_BEGUN,
      number_of_files: totalUploads,
    });

    usePrismItemStore.setState(() => ({
      imagesUploadProgress: {
        current: 1,
        totalFiles: totalUploads,
        // Carried through to the UPLOAD_DONE dispatch in trackProgress so the
        // session tracker can de-dupe and separate source-files from images.
        batchId,
        sourceFiles: filteredFiles.length,
      },
    }));
    
    const queue = [...filteredFiles];
    const worker = async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        try {
          if (file.type === 'application/pdf') {
            await processPDF(file, prismItemID);
          } else {
            await processImage(file, prismItemID);
          }
        } catch (error) {
          dispatchAndDisplayFailedUpload(file.name);
          console.error('Error processing file:', error);
        }
      }
    };
    // Setting the number here sets the concurrency value.
    await Promise.all(Array.from({ length: Math.min(3, filteredFiles.length) }, worker));
  }
};

const getTotalUploadCount = async (files) => {
  let total = 0;

  for (const file of files) {
    if (file.type === 'application/pdf') {
      try {
        ensurePdfWorker();
        const pdfData = await readFileAsArrayBuffer(file);
        const pdfDoc = await pdfjsLib.getDocument({ data: pdfData }).promise;
        total += pdfDoc.numPages;
      } catch (e) {
        console.error('Failed to read PDF page count', file.name, e);
        total += 1; // fallback so progress doesn’t stall
      }
    } else {
      total += 1;
    }
  }

  return total;
};


// --------------------------------------------------------------------

/**
 * A reusable photo upload button component that handles file selection and processing.
 * Supports both direct file upload (desktop) and modal-based upload (mobile).
 *
 * @param {Object} props - Component props
 * @param {boolean} props.pending - Whether an upload is currently in progress
 * @param {Object} props.progress - Upload progress object with current and total counts
 * @param {Function} props.onUploadStarted - Callback when upload starts (optional)
 * @param {string} props.variant - Button variant: 'desktop', 'mobile', or 'auto' (default)
 * @param {string} props.className - Additional CSS classes (optional)
 * @param {boolean} props.unstyled - If true, uses only the provided className without default styles
 * @param {string} props.label - Custom button label (optional)
 * @param {string} props.ariaLabel - Custom aria-label for accessibility (optional, falls back to label)
 * @param {string} props.icon - Custom icon class (optional, defaults to 'fas fa-cloud-upload-alt')
 * @param {React.Ref} props.buttonRef - Ref to attach to the button element (optional)
 */
const PhotoUploadButton = ({
  pending = false,
  progress = null,
  onUploadStarted,
  variant = 'auto',
  className = '',
  unstyled = false,
  label,
  ariaLabel,
  icon = 'fas fa-cloud-upload-alt',
  buttonRef,
  disabled = false,
}) => {
  const fileInputRef = useRef(null);
  const { isMobile } = getViewport();
  const resolvedVariant = variant === 'auto' ? (isMobile ? 'mobile' : 'desktop') : variant;

  const handleFileInputChange = useCallback(async (e) => {
    const $fileInput = e.target;
    const files = Array.from($fileInput.files);

    if (!files.length) return;

    if (onUploadStarted) {
      onUploadStarted();
    }

    await addFiles(files);
    $fileInput.value = '';
  }, [onUploadStarted]);

  const handleMobileClick = useCallback(() => {
    if (onUploadStarted) {
      onUploadStarted();
    }
    fireAddPhotos();
  }, [onUploadStarted]);

  const handleDesktopClick = useCallback(() => {
    // Click the hidden file input using ref
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      // Click the hidden file input using ref
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  }, []);

  // Default class names for each variant
  const defaultMobileClasses =
    'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-bg-white tw-border-none tw-text-black tw-font-extrabold tw-text-sm';
  const defaultDesktopClasses = 'btn btn-light btn-block add-btn add-btn-sm-compact add-photos';

  // Resolve aria-label: use ariaLabel prop, fall back to label, then default
  const resolvedAriaLabel = ariaLabel || label || 'Add Photos';
  const processingAriaLabel = ariaLabel ? `Processing photos - ${ariaLabel}` : 'Processing photos';

  // Loading state - show progress indicator
  if (pending) {
    const loadingClasses = unstyled
      ? className
      : `${resolvedVariant === 'mobile' ? defaultMobileClasses : defaultDesktopClasses} ${className}`;

    return (
      <button
        aria-label={processingAriaLabel}
        type="button"
        className={loadingClasses}
        disabled
        ref={buttonRef}
      >
        <div className="tw-flex tw-gap-2 tw-items-center tw-justify-center">
          {progress?.total > 1 && (
            <span className="tw-hidden lg:tw-inline">
              Processing ({progress.current} of {progress.total})
            </span>
          )}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="tw-w-1 tw-h-1 tw-rounded-full tw-animate-pulse tw-bg-black"
            />
          ))}
        </div>
      </button>
    );
  }

  // Mobile variant - opens modal upload
  if (resolvedVariant === 'mobile') {
    const mobileClasses = unstyled
      ? className
      : `${defaultMobileClasses} ${className}`;
    return (
      <div>
        <button
          type="button"
          aria-label={resolvedAriaLabel}
          className={mobileClasses}
          ref={buttonRef}
          disabled={pending || disabled}
          onClick={handleDesktopClick}>
          <div className="tw-flex tw-items-center tw-justify-center tw-bg-gray-100 tw-rounded-full tw-h-10 tw-w-10">
            <i className="fas fa-mobile-alt tw-text-gray-600" />
          </div>
          <span className="tw-text-xs tw-text-gray-600 tw-text-center">
            Upload
            <br />
            Photos
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="tw-hidden"
          multiple
          accept={fileValidator.acceptFileTypeString}
          onChange={handleFileInputChange}
          aria-label={resolvedAriaLabel}
        />
      </div>
    );
  }

  // Desktop variant - direct file upload
  const desktopClasses = unstyled ? className : `${defaultDesktopClasses} ${className}`;

  return (
    <div>
      <button
        ref={buttonRef}
        aria-label={resolvedAriaLabel}
        className={desktopClasses}
        tabIndex="0"
        onClick={handleDesktopClick}
        onKeyDown={handleKeyDown}
        disabled={pending || disabled}>
        <span className="tw-mr-2 tw-font-bold">{label || 'Upload Photos'}</span>
        <i className={icon} />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        className="tw-hidden"
        multiple
        accept={fileValidator.acceptFileTypeString}
        onChange={handleFileInputChange}
        aria-label={resolvedAriaLabel}
      />
    </div>
  );
};

PhotoUploadButton.propTypes = {
  pending: PropTypes.bool,
  progress: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
  }),
  onUploadStarted: PropTypes.func,
  variant: PropTypes.oneOf(['auto', 'mobile', 'desktop']),
  className: PropTypes.string,
  unstyled: PropTypes.bool,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  icon: PropTypes.string,
  buttonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  disabled: PropTypes.bool,
};

// Export utility functions for reuse elsewhere if needed
export {
  addFiles,
  processImage,
  processPDF,
  convertHEICIfNeeded,
  processFileAndUpload,
  dispatchAndDisplayFailedUpload,
};

export default PhotoUploadButton;
